import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createAccountVendor, updateAccountVendor, getAccountVendorById } from '../core/_requests';
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewVendor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const vendorId = id ? Number(id) : null;
  const isEditing = vendorId !== null && !Number.isNaN(vendorId);

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Form State
  const [vendorType, setVendorType] = useState('Business');
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [pan, setPan] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [paymentTerms, setPaymentTerms] = useState('Due on Receipt');
  const [openingBalance, setOpeningBalance] = useState('');
  const [billingStreet1, setBillingStreet1] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingPincode, setBillingPincode] = useState('');

  useEffect(() => {
    if (!isEditing || vendorId == null) return;
    setLoadingInitial(true);
    getAccountVendorById(vendorId).then(data => {
      if (data) {
        setVendorType(data.vendorType || 'Business');
        setSalutation(data.salutation || '');
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setCompanyName(data.companyName || '');
        setDisplayName(data.displayName || '');
        setEmail(data.email || '');
        setPhone(data.workPhone || '');
        setMobile(data.mobile || '');
        setPan(data.pan || '');
        setCurrency(data.currency || 'INR');
        setPaymentTerms(data.paymentTerms || 'Due on Receipt');
        setOpeningBalance(data.openingBalance !== undefined ? String(data.openingBalance) : '');
        setBillingStreet1(data.billingStreet1 || '');
        setBillingCity(data.billingCity || '');
        setBillingPincode(data.billingPincode || '');
      }
    }).catch(console.error).finally(() => setLoadingInitial(false));
  }, [isEditing, vendorId]);

  const handleSave = async () => {
    if (!displayName.trim()) {
      alert("Display Name is required!");
      return;
    }

    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        alert("Please enter a valid email address!");
        return;
      }
    }
    
    setSaving(true);
    const payload = {
      vendorType,
      salutation,
      firstName,
      lastName,
      companyName,
      displayName,
      email,
      workPhone: phone,
      mobile,
      pan,
      currency,
      paymentTerms,
      openingBalance: openingBalance ? Number(openingBalance) : 0,
      billingStreet1,
      billingCity,
      billingPincode,
    };

    try {
      if (isEditing && vendorId) {
        await updateAccountVendor(vendorId, payload);
      } else {
        await createAccountVendor(payload);
      }
      navigate('/accounts/purchase/vendors');
    } catch (err: any) {
      console.error('Save failed:', err);
      alert('Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all";

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Vendor...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Purchase / Vendors</div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? `Edit Vendor: ${displayName}` : 'New Vendor'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? 'Update vendor details' : 'Create a new vendor profile'}
                </p>
              </div>
              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="flex items-center gap-4 mb-5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="vendorType" value="Business" checked={vendorType === 'Business'} onChange={() => setVendorType('Business')} className="accent-emerald-500 w-4 h-4" /> 
                    Business
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="vendorType" value="Individual" checked={vendorType === 'Individual'} onChange={() => setVendorType('Individual')} className="accent-emerald-500 w-4 h-4" /> 
                    Individual
                  </label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Salutation</label>
                    <SearchableSelect value={salutation} onChange={e => setSalutation(e.target.value)} className={inputClass}>
                      <option value="">Select...</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">First Name</label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Last Name</label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Company Name</label>
                    <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company Name" className={inputClass} />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Display Name <span className="text-red-500">*</span></label>
                  <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Display Name" required className={inputClass} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vendor@example.com" className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Work Phone</label>
                    <input type="text" value={phone} maxLength={10} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Work Phone" className={inputClass} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Mobile</label>
                    <input type="text" value={mobile} maxLength={10} onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Mobile Number" className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">PAN</label>
                    <input type="text" value={pan} onChange={e => setPan(e.target.value)} placeholder="PAN Number" className={inputClass} />
                  </div>
                </div>

                <hr className="my-6 border-slate-100" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Currency</label>
                    <SearchableSelect value={currency} onChange={e => setCurrency(e.target.value)} className={inputClass}>
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Payment Terms</label>
                    <SearchableSelect value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} className={inputClass}>
                      <option value="Due on Receipt">Due on Receipt</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                    </SearchableSelect>
                  </div>
                </div>
                
                <div className="mb-5 max-w-md">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Opening Balance</label>
                  <input 
                    type="number" 
                    value={openingBalance} 
                    onChange={e => setOpeningBalance(e.target.value)} 
                    min="0"
                    onKeyDown={e => {
                      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="0.00" 
                    className={inputClass} 
                  />
                </div>
                
                <hr className="my-6 border-slate-100" />
                
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Billing Address - Street</label>
                  <input type="text" value={billingStreet1} onChange={e => setBillingStreet1(e.target.value)} placeholder="Street 1" className={inputClass} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">City</label>
                    <input type="text" value={billingCity} onChange={e => setBillingCity(e.target.value)} placeholder="City" className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Pincode</label>
                    <input type="text" value={billingPincode} maxLength={10} onChange={e => setBillingPincode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))} placeholder="Pincode" className={inputClass} />
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Vendor'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

